import React, { useState, useCallback } from 'react';
import Icon, { DownOutlined, RightOutlined } from '@ant-design/icons';
import EditSvg from '@icons/event-management/edit.svg';
import DeleteSvg from '@icons/event-management/delete-blue.svg';
import { Button, Tooltip } from 'antd';
import {
  CollapsedEventNameWrapper,
  EventKeyWrapper,
  EventRightWrapper,
  EventLeftWrapper,
  EventItemActionsWrapper,
  RuleText,
  ANDText,
} from '@src/emp-3.0/components/Metrics/Flow/FlowForm/styled';

// Utility function to extract network_request_url_path value from mapping rules
const extractNetworkRequestUrlPath = (networkRequest: any): string => {
  try {
    const rules = networkRequest?.rules || [];

    // Handle new structure: networkRequest.rules is [[[rule]]]
    for (const ruleGroup of rules) {
      if (Array.isArray(ruleGroup)) {
        for (const rule of ruleGroup) {
          if (rule?.mapping_rules) {
            for (const mappingRuleGroup of rule.mapping_rules) {
              if (Array.isArray(mappingRuleGroup)) {
                for (const mappingRule of mappingRuleGroup) {
                  if (
                    mappingRule?.tag_key?.value?.includes('network_request_url_path') &&
                    mappingRule?.tag_value?.value?.length > 0
                  ) {
                    return mappingRule.tag_value.value.join(', ');
                  }
                }
              }
            }
          }
        }
      }
    }

    return 'Network Request';
  } catch (error) {
    // Error in extracting network request URL path, fallback to default
    return 'Network Request';
  }
};

interface IFlowNWRReadModeProps {
  networkRequest: any;
  expanded?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
}

const FlowNWRReadMode: React.FC<IFlowNWRReadModeProps> = React.memo(
  ({ networkRequest, expanded = false, onEdit, onDelete, canEdit = false }) => {
    const [isExpanded, setExpanded] = useState(expanded);
    const [isHovered, setIsHovered] = useState(false);

    const toggleExpanded = useCallback(() => {
      setExpanded(!isExpanded);
    }, [isExpanded]);

    const editButton = (
      <Tooltip title="Edit">
        <Button
          type={'text'}
          style={{ color: 'rgba(0, 0, 0, 0.45)' }}
          icon={<Icon component={EditSvg} style={{ fontSize: '12px' }} />}
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.();
          }}
        />
      </Tooltip>
    );

    const getOperator = (op: any) => {
      return op || 'eq';
    };

    const renderRulesPreview = () => {
      // Handle both old and new structure: networkRequest.rules can be [[[rule]]] or old format
      const rulesArray = networkRequest?.rules || [];

      if (rulesArray.length === 0) {
        return (
          <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontStyle: 'italic' }}>
            No mapping rules configured
          </div>
        );
      }

      // Check if we're dealing with the old structure (rules.value and rules.mapping_rules)
      if (rulesArray.value && rulesArray.mapping_rules) {
        // Old structure: convert to new structure for processing
        const mappingRules = rulesArray.mapping_rules || [];

        if (mappingRules.length === 0) {
          return (
            <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontStyle: 'italic' }}>
              No mapping rules configured
            </div>
          );
        }

        return mappingRules.map((rowMappingRule, rowMappingIndex) => {
          // Handle both array and single rule structures
          const rulesArray = Array.isArray(rowMappingRule) ? rowMappingRule : [rowMappingRule];

          return (
            <React.Fragment key={rowMappingIndex}>
              {rowMappingIndex !== 0 && (
                <div
                  style={{
                    display: 'flex',
                    padding: '10px 0px',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '77px',
                      height: '2px',
                      background: 'rgba(0, 0, 0, 0.06)',
                    }}
                  />
                  <ANDText>AND</ANDText>
                  <div
                    style={{
                      width: '180px',
                      height: '2px',
                      background: 'rgba(0, 0, 0, 0.06)',
                    }}
                  />
                </div>
              )}
              {rulesArray.map((subRule, subRuleIndex) => (
                <div
                  key={subRuleIndex}
                  style={{ display: 'flex', paddingBottom: '10px', width: '100%' }}
                >
                  {subRuleIndex === 0 ? null : (
                    <RuleText
                      width={'30px'}
                      style={{
                        color: 'rgba(0, 0, 0, 0.45)',
                        minWidth: '30px',
                        marginLeft: '-30px',
                      }}
                    >
                      {'OR'}
                    </RuleText>
                  )}
                  <RuleText width={subRuleIndex === 0 ? '100%' : 'calc(100% - 30px)'}>
                    <span style={{ paddingRight: '10px' }}>
                      {subRule?.tag_key?.value?.join?.() ||
                        (typeof subRule?.tag_key === 'string' ? subRule?.tag_key : '')}
                    </span>
                    {(subRule?.tag_key?.value?.join?.() ||
                      typeof subRule?.tag_key === 'string') && (
                      <span
                        style={{
                          paddingRight: '10px',
                          color: 'rgba(0, 0, 0, 0.45)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getOperator(subRule?.tag_value?.op || subRule?.operator)}
                      </span>
                    )}
                    <span style={{ paddingRight: '10px' }}>
                      {subRule?.tag_value?.value?.join?.() ||
                        (typeof subRule?.tag_value === 'string' ? subRule?.tag_value : '')}
                    </span>
                  </RuleText>
                </div>
              ))}
            </React.Fragment>
          );
        });
      }

      // New structure: networkRequest.rules is [[[rule]]]
      // Get the first rule group and then the first rule
      const ruleGroup = rulesArray[0] || [];
      const rule = ruleGroup[0] || {};
      const mappingRules = rule.mapping_rules || [];

      if (mappingRules.length === 0) {
        return (
          <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontStyle: 'italic' }}>
            No mapping rules configured
          </div>
        );
      }

      return mappingRules.map((rowMappingRule, rowMappingIndex) => {
        // Handle both array and single rule structures
        const rulesArray = Array.isArray(rowMappingRule) ? rowMappingRule : [rowMappingRule];

        return (
          <React.Fragment key={rowMappingIndex}>
            {rowMappingIndex !== 0 && (
              <div
                style={{
                  display: 'flex',
                  padding: '10px 0px',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    width: '77px',
                    height: '2px',
                    background: 'rgba(0, 0, 0, 0.06)',
                  }}
                />
                <ANDText>AND</ANDText>
                <div
                  style={{
                    width: '180px',
                    height: '2px',
                    background: 'rgba(0, 0, 0, 0.06)',
                  }}
                />
              </div>
            )}
            {rulesArray.map((subRule, subRuleIndex) => (
              <div
                key={subRuleIndex}
                style={{ display: 'flex', paddingBottom: '10px', width: '100%' }}
              >
                {subRuleIndex === 0 ? null : (
                  <RuleText
                    width={'30px'}
                    style={{
                      color: 'rgba(0, 0, 0, 0.45)',
                      minWidth: '30px',
                      marginLeft: '-30px',
                    }}
                  >
                    {'OR'}
                  </RuleText>
                )}
                <RuleText width={subRuleIndex === 0 ? '100%' : 'calc(100% - 30px)'}>
                  <span style={{ paddingRight: '10px' }}>
                    {subRule?.tag_key?.value?.join?.() ||
                      (typeof subRule?.tag_key === 'string' ? subRule?.tag_key : '')}
                  </span>
                  {(subRule?.tag_key?.value?.join?.() || typeof subRule?.tag_key === 'string') && (
                    <span
                      style={{
                        paddingRight: '10px',
                        color: 'rgba(0, 0, 0, 0.45)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getOperator(subRule?.tag_value?.op || subRule?.operator)}
                    </span>
                  )}
                  <span style={{ paddingRight: '10px' }}>
                    {subRule?.tag_value?.value?.join?.() ||
                      (typeof subRule?.tag_value === 'string' ? subRule?.tag_value : '')}
                  </span>
                </RuleText>
              </div>
            ))}
          </React.Fragment>
        );
      });
    };

    return (
      <div
        style={{
          width: '100%',
          minWidth: '500px',
        }}
        className="flow-nwr-read-mode"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CollapsedEventNameWrapper onClick={toggleExpanded}>
          <div
            style={{
              textAlign: 'left',
              paddingRight: '5px',
              display: 'flex',
              flex: 1,
              alignItems: 'center',
            }}
          >
            {isExpanded ? (
              <DownOutlined style={{ color: 'rgb(0,0,0,0.45)' }} />
            ) : (
              <RightOutlined style={{ color: 'rgb(0,0,0,0.45)' }} />
            )}
            <div style={{ paddingLeft: '8px', fontWeight: '500' }}>Path</div>
            <div
              style={{
                paddingLeft: '8px',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {extractNetworkRequestUrlPath(networkRequest)}
            </div>
          </div>
          {canEdit && isHovered ? (
            <EventItemActionsWrapper
              className="event-item-actions"
              style={{
                opacity: 1,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              {' '}
              {editButton}
              <div
                style={{ width: '1px', height: '16px', background: '#D9D9D9', margin: '0 4px' }}
              ></div>
              <Tooltip title="Delete">
                <Button
                  type={'text'}
                  style={{ color: 'rgba(0, 0, 0, 0.45)' }}
                  icon={<Icon component={DeleteSvg} style={{ fontSize: '12px' }} />}
                  onClick={(e) => {
                    onDelete?.();
                    e.stopPropagation();
                  }}
                />
              </Tooltip>
            </EventItemActionsWrapper>
          ) : null}
        </CollapsedEventNameWrapper>

        {/* Preview section when expanded */}
        {isExpanded && (
          <div style={{ width: '100%', padding: '0 24px 0 30px', borderTop: 'solid 1px #d9d9d9' }}>
            <EventKeyWrapper>
              <EventLeftWrapper className="event-left" style={{ fontWeight: '500' }}>
                Event
              </EventLeftWrapper>
              <EventRightWrapper style={{ fontWeight: '500' }}>Tag Rule</EventRightWrapper>
            </EventKeyWrapper>
            <EventKeyWrapper>
              <EventLeftWrapper className="event-left" style={{ marginBottom: '10px' }}>
                conviva_network_request
              </EventLeftWrapper>
              <EventRightWrapper>{renderRulesPreview()}</EventRightWrapper>
            </EventKeyWrapper>
          </div>
        )}
      </div>
    );
  },
);

FlowNWRReadMode.displayName = 'FlowNWRReadMode';

export default FlowNWRReadMode;
